import React, { useState, useEffect, useRef } from 'react'
import { Box, IconButton, Typography } from '@mui/material'
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
} from '@mui/icons-material'

export const WAVEFORM_BARS = [3, 6, 9, 5, 12, 8, 4, 10, 7, 14, 6, 9, 4, 11, 7, 3, 8, 12, 5, 9, 4, 7, 10, 5, 8, 3, 6, 11, 7, 4]

function SeekableWaveform({ progress = 0, onSeek }: { progress?: number; onSeek: (f: number) => void }) {
  const ref = useRef<HTMLDivElement>(null)
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = ref.current?.getBoundingClientRect()
    if (!rect) return
    onSeek(Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)))
  }
  return (
    <Box
      ref={ref}
      onClick={handleClick}
      sx={{ display: 'flex', alignItems: 'center', gap: '2px', height: 22, flex: 1, cursor: 'pointer' }}
    >
      {WAVEFORM_BARS.map((h, i) => (
        <Box
          key={i}
          sx={{
            width: 2,
            height: h,
            borderRadius: 1,
            flexShrink: 0,
            bgcolor: i / WAVEFORM_BARS.length <= progress ? '#7b1fa2' : '#9ca3af',
            opacity: i / WAVEFORM_BARS.length <= progress ? 1 : 0.6,
            transition: 'background-color 0.05s',
          }}
        />
      ))}
    </Box>
  )
}

function fmt(sec: number | null | undefined): string {
  if (sec == null) return '0:00'
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

interface AudioPlayerProps {
  recordingUrl: string
  durationSeconds?: number
}

export function AudioPlayer({ recordingUrl, durationSeconds }: AudioPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [progress, setProgress] = useState(0)
  const [currentSec, setCurrentSec] = useState(0)
  const [totalSec, setTotalSec] = useState(durationSeconds ?? 0)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  useEffect(() => () => {
    audioRef.current?.pause()
    audioRef.current = null
  }, [])

  const getAudio = () => {
    if (audioRef.current) return audioRef.current
    const audio = new Audio(recordingUrl)
    audio.addEventListener('loadedmetadata', () => {
      if (isFinite(audio.duration)) setTotalSec(Math.round(audio.duration))
    })
    audio.addEventListener('timeupdate', () => {
      const dur = isFinite(audio.duration) && audio.duration > 0 ? audio.duration : (durationSeconds ?? 1)
      setCurrentSec(Math.floor(audio.currentTime))
      setProgress(audio.currentTime / dur)
    })
    audio.addEventListener('ended', () => {
      setIsPlaying(false)
      setProgress(0)
      setCurrentSec(0)
    })
    audioRef.current = audio
    return audio
  }

  const handlePlayPause = () => {
    const audio = getAudio()
    if (isPlaying) {
      audio.pause()
      setIsPlaying(false)
    } else {
      audio.play().catch(() => {})
      setIsPlaying(true)
    }
  }

  const handleSeek = (fraction: number) => {
    const audio = getAudio()
    const dur = isFinite(audio.duration) && audio.duration > 0 ? audio.duration : (totalSec || 1)
    audio.currentTime = fraction * dur
    setProgress(fraction)
    setCurrentSec(Math.floor(fraction * dur))
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 160 }}>
      <IconButton
        size="small"
        onClick={handlePlayPause}
        sx={{
          width: 28,
          height: 28,
          bgcolor: '#1f2937',
          color: '#fff',
          flexShrink: 0,
          '&:hover': { bgcolor: '#374151' },
        }}
      >
        {isPlaying ? <PauseIcon sx={{ fontSize: 14 }} /> : <PlayIcon sx={{ fontSize: 14, ml: '1px' }} />}
      </IconButton>
      <SeekableWaveform progress={progress} onSeek={handleSeek} />
      <Typography
        fontSize="11px"
        color="text.secondary"
        sx={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums', minWidth: 56, textAlign: 'right' }}
      >
        {fmt(isPlaying || progress > 0 ? currentSec : null)} / {fmt(totalSec)}
      </Typography>
    </Box>
  )
}
